import {ReactElement, useMemo, useState} from "react";
import {
    AllPropertyForecastLocksQuery,
    useCreateWorksheetLinesMutation,
    VersionType
} from "../../../__generated__/generated_types";
import {useChartOfAccounts} from "../../../contexts/chartofaccounts/ChartOfAccountsContext";
import {Field as FormField, Input as FormInput, Label as FormLabel} from "@zendeskgarden/react-forms";
import {Button} from "@zendeskgarden/react-buttons";
import {toast} from "react-toastify";
import {TooltipModal} from "@zendeskgarden/react-modals";
import {Inline} from "@zendeskgarden/react-loaders";
import {COLORS} from "../../../constants/Colors";
import {CustomZDDropdown} from "../../../atoms/custom-zd-dropdown/CustomZDDropdown";
import {toLockLookup} from "./logic";
import {Property} from "../../../contexts/properties/logic";

export interface CreateLineItemTooltipModalProps {
    budgetYear: number;
    versionType: VersionType.Budget | VersionType.Reforecast;
    properties: Property[];
    locksLookup: Set<string>;
    referenceElement: HTMLButtonElement;
    onClose: () => void;
    onCreated: () => void;
}

export function CreateLineItemTooltipModal(props: CreateLineItemTooltipModalProps): ReactElement {
    const {
        budgetYear,
        versionType,
        properties,
        locksLookup,
        referenceElement,
        onClose,
        onCreated
    } = props;

    const [createWorksheetLines, {loading: createLoading}] = useCreateWorksheetLinesMutation();
    const [description, setDescription] = useState<string>("");
    const [selectedPropertyIds, setSelectedPropertyIds] = useState<string[]>([]);
    const [selectedAccountId, setSelectedAccountId] = useState<string | undefined>(undefined);

    const {accounts} = useChartOfAccounts();

    const unlockedProperties = useMemo(() => properties.filter(p => !locksLookup.has(p.id)),[properties, locksLookup]);
    const allPropertiesLocked = unlockedProperties.length === 0;

    function handleSubmit(): void {
        if (!description || selectedPropertyIds.length === 0 || !selectedAccountId) {
            toast.error("Please fill in all fields");
            return;
        }

        createWorksheetLines({
            variables: {
                budgetYear,
                versionTypes: [versionType],
                propertyIds: selectedPropertyIds,
                destinationAccountId: selectedAccountId,
                description
            }
        }).then(res => {
            if (res.data?.createWorksheetLines) {
                toast.success("Line item created successfully");
                onCreated();
            } else {
                toast.error("Failed to create line item");
            }
        }).catch(() => {
            toast.error("Failed to create line item");
        });
    }

    return (
        <TooltipModal
            referenceElement={referenceElement}
            onClose={onClose}
            placement="start-top"
        >
            <TooltipModal.Title>Create Line Item</TooltipModal.Title>
            <TooltipModal.Body>
                <div style={{display: "flex", flexDirection: "column", gap: "0.75rem", minWidth: "20rem"}}>
                    <FormField>
                        <FormLabel>Description</FormLabel>
                        <FormInput
                            value={description}
                            onChange={e => setDescription(e.target.value)}
                        />
                    </FormField>
                    <CustomZDDropdown
                        label="Properties"
                        allOption
                        isMulti
                        options={unlockedProperties.map(p => ({value: p.id, label: p.name}))}
                        initialSelectedItems={selectedPropertyIds}
                        applySelectedItems={items => setSelectedPropertyIds(items)}
                        openDropdownPlaceholder="Search properties..."
                        closedDropdownPlaceholder="Select properties"
                        isError={undefined}
                    />
                    <CustomZDDropdown
                        label="Destination Account"
                        options={accounts.map(a => ({value: a.id, label: `${a.accountNumber} - ${a.name}`}))}
                        initialSelectedItems={selectedAccountId ? [selectedAccountId] : []}
                        applySelectedItems={items => setSelectedAccountId(items[0])}
                        openDropdownPlaceholder="Search accounts..."
                        closedDropdownPlaceholder="Select an account"
                        isError={undefined}
                    />
                    {allPropertiesLocked &&
                        <span style={{color: COLORS.NEGATIVE_COLOR, fontSize: "0.85rem"}}>
                            All properties are locked for the selected forecast period.
                        </span>
                    }
                    <Button
                        isPrimary
                        size="small"
                        onClick={handleSubmit}
                        disabled={allPropertiesLocked || createLoading || !description || selectedPropertyIds.length === 0 || !selectedAccountId}
                    >
                        {createLoading
                            ? <Inline size={24} color={COLORS.POSITIVE_COLOR} aria-label="loading" />
                            : <span>Create</span>
                        }
                    </Button>
                </div>
            </TooltipModal.Body>
            <TooltipModal.Close aria-label="Close" />
        </TooltipModal>
    );
}
